/**
 * ReadOnlyGuard
 * ─────────────
 * Wrapper qui applique un mode "aperçu strict" autour du contenu d'une page
 * lorsque l'utilisateur n'a pas le droit `write` sur le module donné, OU
 * lorsqu'un verrou pessimiste empêche l'édition.
 *
 * Effet :
 *   • <fieldset disabled> autour des enfants → tous les inputs/buttons natifs
 *     sont désactivés (HTML standard).
 *   • Attribut `data-readonly-strict="true"` → règles CSS dans App.css qui
 *     **masquent** les boutons d'écriture (Pencil, Trash, Plus, Save…) et
 *     les listes d'actions.
 *   • Bandeau "Mode lecture seule" en haut, sauf si la cause est un verrou
 *     pessimiste (un autre bandeau LockBanner s'en charge dans ce cas).
 *
 * Usage :
 *   <ReadOnlyGuard module="clients" lockBlocked={lock.blocked}>
 *     ... contenu de la page ...
 *   </ReadOnlyGuard>
 */
import { usePermissions } from '../../contexts/PermissionsContext';
import { ReadOnlyLockBadge } from './ReadOnlyLockBadge';

export function ReadOnlyGuard({ module: moduleKey, lockBlocked = false, forceReadOnly = false, children }) {
    const { can, loading } = usePermissions();
    if (loading) {
        // Pendant le chargement des permissions on n'autorise pas les écritures
        // pour éviter les flashs de boutons puis disparition.
        return (
            <fieldset disabled className="contents" data-readonly-strict="true" data-readonly-loading="true">
                {children}
            </fieldset>
        );
    }
    const noWrite = !can(moduleKey, 'write');
    const strict = noWrite || lockBlocked || forceReadOnly;
    if (!strict) return <>{children}</>;
    return (
        <>
            {noWrite && !lockBlocked && <ReadOnlyLockBadge />}
            <fieldset disabled className="contents" data-readonly-strict="true" data-testid={`readonly-guard-${moduleKey}`}>
                {children}
            </fieldset>
        </>
    );
}
